import React, { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styles from './MonthlyUpgradeSelection.module.scss';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import Button from '../../components/Button';
import { SubscriptionOption } from '../../components/SubscriptionOptionDetails/SubscriptionOptionDetails';
import { useSubscriptionCartMode } from '../../lib/cartModes';
import { gqlBillingCadenceToBillingCadence } from '../../lib/subscription';
import { cartActions } from '../../reducers/subscriptionCart';
import { useSubscriptionChooserGroups } from '../../components/SubscriptionOptions/SubscriptionOptions';
import Chooser from '../../components/Chooser';
import AppPaths from '../../AppPaths';
import {
  centsToDollars,
  centsToDollarsNumber,
  getLoadingOrErrorElement,
  getLocalizedPricing,
  getPriceForRateUnit,
  mkError,
} from '../../lib/util';
import classNames from 'classnames';
import { BillingCadence, gqlTypes } from '../../types';
import MonthlyUpgradeConfirmationModal from './MonthlyUpgradeConfirmationModal';
import { useMutation } from '@apollo/client';
import { purchaseMonthlyUpgradeMutation } from '../../graphql-operations/MonthlyUpgrade.graphql';
import { PurchaseSubscriptionCompleteState } from '../PurchaseSubscription/PurchaseSubscriptionComplete/PurchaseSubscriptionComplete';
import { DateTime } from 'luxon';
import ErrorMessage, { useError } from '../../components/ErrorMessage';
import { monthlyUpgrade as events } from '../../lib/analytics/events';
import { getFiGrowthBook } from '../../lib/growthbook';
import { Image } from '../../components/Image';
import { applyCouponToCart, isValidCodeResult } from '../../lib/promoCode';
import { MonthlyUpgradeCompleteState } from './MonthlyUpgradeComplete';

import CollarHero from '../../assets/images/collar_hero_blue_3.png';
import Check from '../../assets/images/icons/check_green.svg';
import { useLocale } from '../../contexts/LocaleContext';

const SIMPLIFIED_OFFER_TARGET_RENEWAL_MONTHS = 6;

function getSubscriptionDescription(subscription: SubscriptionOption, simplifiedOffer: boolean, t: any): string {
  if (subscription.renewalMonths === 6) {
    return simplifiedOffer ? t('6_month') : t('6_month_membership');
  } else if (subscription.renewalMonths === 12) {
    return t('1_year_membership');
  } else if (subscription.renewalMonths === 24) {
    return t('2_year_membership');
  } else {
    return subscription.name;
  }
}

function getNumFreeMonthByRenewalMonths(renewalMonths: number): number {
  const planToFreeMonthsMap: Record<number, number> = {
    6: 1,
    12: 2,
    24: 3,
  };

  return planToFreeMonthsMap[renewalMonths] ?? 0;
}

export default function MonthlyUpgradeSelection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { device, couponCode } = usePurchaseSubscriptionContext();
  const { cartSelector } = useSubscriptionCartMode();
  const cart = useSelector(cartSelector);
  const { error, errorID, setError } = useError();
  const [upgradeClicked, setUpgradeClicked] = useState(false);
  const { currency, currencyFormatter } = useLocale();
  events.viewed();

  const applicableSubscriptionProducts = device.purchasableSubscriptionOptions.reduce(
    (applicableProducts: SubscriptionOption[], subscriptionOption) => {
      applicableProducts.push({
        ...subscriptionOption,
        billingCadence: gqlBillingCadenceToBillingCadence(subscriptionOption.billingCadence),
      });
      return applicableProducts;
    },
    [],
  );

  const selectedSubscriptionSku = useMemo(() => {
    const cartItem = Object.values(cart.cartItems)[0];
    if (!cartItem) {
      return undefined;
    }

    return cartItem.lineItem.sku;
  }, [cart.cartItems]);

  const selectedSubscriptionOption = useMemo(() => {
    return applicableSubscriptionProducts.find((option) => option.sku === selectedSubscriptionSku);
  }, [applicableSubscriptionProducts, selectedSubscriptionSku]);

  const appliedCouponCode = useMemo(() => {
    return cart.couponCode;
  }, [cart.couponCode]);

  const chooserGroups = useSubscriptionChooserGroups({
    subscriptionProducts: applicableSubscriptionProducts,
    monthlyUpgrade: true,
    showSubscriptionLengthDiscount: true,
  });

  const simplifiedOffer = getFiGrowthBook().getFeatureValue<boolean>('in-app-mtm-6-month-upsell', false);
  const currentMTMPriceCents =
    (device.subscription?.subscriptionOption?.priceInCents ?? 0) * (selectedSubscriptionOption?.renewalMonths ?? 1);
  const selectedPriceInCents = selectedSubscriptionOption
    ? getLocalizedPricing(selectedSubscriptionOption.pricingInCents, currency)
    : undefined;
  const chargedTodayCents = selectedSubscriptionOption?.planSwitchAmountChargedInCents ?? selectedPriceInCents ?? 0;
  const creditAppliedCents = selectedSubscriptionOption?.planSwitchCreditAppliedInCents ?? 0;
  const monthlyCostCents = chargedTodayCents / (selectedSubscriptionOption?.renewalMonths ?? 1);
  const hasDiscount = chargedTodayCents < currentMTMPriceCents;

  const [purchaseMonthlyUpgrade, purchaseMonthlyUpgradeState] = useMutation<
    gqlTypes.ECOMMERCE_purchaseMonthlyUpgrade,
    gqlTypes.ECOMMERCE_purchaseMonthlyUpgradeVariables
  >(purchaseMonthlyUpgradeMutation, {
    onCompleted: (data) => {
      const state: PurchaseSubscriptionCompleteState = {
        subscriptionProduct: selectedSubscriptionOption,
        showAppBar: false,
      };
      const simpleState: MonthlyUpgradeCompleteState = {
        chargedTodayCents,
        originalPeriodCostCents: currentMTMPriceCents,
        creditAppliedCents,
        subscriptionDescription: selectedPlanDescription,
        subscriptionProduct: selectedSubscriptionOption,
      };
      events.successfullyUpgraded({ newSku: selectedSubscriptionSku! });
      if (simplifiedOffer) {
        history.push({
          pathname: AppPaths.MonthlyUpgrade.CompleteV2(device.moduleId),
          state: simpleState,
          search: window.location.search,
        });
      } else {
        history.push({
          pathname: AppPaths.MonthlyUpgrade.Complete(device.moduleId),
          state,
          search: window.location.search,
        });
      }
    },
    onError(err) {
      setError(err.message);
    },
  });

  // We'd expect that if they're on this screen to upgrade from a monthly plan, they have a
  // subscription and a known subscription plan.
  if (!device.subscription?.subscriptionOption) {
    return mkError(t('unknown_subscription_plan'));
  }

  const loadingOrErrorElement = getLoadingOrErrorElement(purchaseMonthlyUpgradeState.loading, null);
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  const upgradeMembership = () => {
    purchaseMonthlyUpgrade({
      variables: {
        input: {
          moduleId: device.moduleId,
          sku: selectedSubscriptionSku!,
          couponCodes: appliedCouponCode ? [appliedCouponCode] : null,
        },
      },
    });
  };

  const checkoutDisabled = selectedSubscriptionOption ? false : true;
  const selectedPlanDescription =
    selectedSubscriptionOption && getSubscriptionDescription(selectedSubscriptionOption, simplifiedOffer, t);

  const { displayRate } = getPriceForRateUnit(
    {
      billingCadence: BillingCadence.Month,
      displayedRate: device.subscription.subscriptionOption.displayedRate,
      priceInCents: device.subscription.subscriptionOption.priceInCents,
      pricingInCents: device.subscription.subscriptionOption.pricingInCents,
      renewalMonths: device.subscription.subscriptionOption.renewalMonths,
    },
    'week',
    currency,
    currencyFormatter,
  );

  const { displayRate: displayRateMonth } = getPriceForRateUnit(
    {
      billingCadence: BillingCadence.Month,
      displayedRate: device.subscription.subscriptionOption.displayedRate,
      priceInCents: device.subscription.subscriptionOption.priceInCents,
      pricingInCents: device.subscription.subscriptionOption.pricingInCents,
      renewalMonths: device.subscription.subscriptionOption.renewalMonths,
    },
    'month',
    currency,
    currencyFormatter,
  );

  /**
   * If user follows through with upgrade, we'll either:
   * a) apply a coupon code and switch now, meaning the renewal date is after one term of the new plan, or
   * b) grant the free months by extending the current monthly plan so renewal date will depend on how many
   * free months they get, which is based on which plan they select.
   */
  const previewRenewalDate = selectedSubscriptionOption
    ? couponCode
      ? DateTime.now().plus({ months: selectedSubscriptionOption.renewalMonths })
      : DateTime.fromISO(device.subscription?.currentTermEndsAt).plus({
          months: getNumFreeMonthByRenewalMonths(selectedSubscriptionOption.renewalMonths),
        })
    : device.subscription?.currentTermEndsAt;

  if (simplifiedOffer) {
    const targetSubscription = applicableSubscriptionProducts.find(
      (s) => s.renewalMonths === SIMPLIFIED_OFFER_TARGET_RENEWAL_MONTHS,
    );

    if (!selectedSubscriptionSku && targetSubscription) {
      dispatch(
        cartActions.setCartItem({
          moduleId: device.moduleId,
          sku: targetSubscription.sku,
          currency,
        }),
      );
      if (couponCode) {
        applyCouponToCart(cart, couponCode).then((couponResult) => {
          if (isValidCodeResult(couponResult)) {
            dispatch(cartActions.addCoupon(couponCode));
          }
        });
      }
    }

    return (
      <>
        <div className={styles.simpleFullWidthWrapper}>
          <div className={styles.simpleContainer}>
            <div className={styles.specialOfferChip}>
              <p>{t('special_offer')}</p>
            </div>
            <div className={styles.simpleHeaderContainer}>
              <h1 className={styles.simpleHeader}>{t('switch_and_save')}</h1>
              <p className={styles.simpleSubheader}>
                {t('switch_from_your_rate_mo_plan_and_get_your_next_months_months_for_only_amount_mo', {
                  rate: displayRateMonth,
                  months: selectedSubscriptionOption?.renewalMonths,
                  amount: centsToDollars(monthlyCostCents),
                })}
              </p>
            </div>
            <div className={styles.offerContainer}>
              <div className={styles.offerDetailsContainer}>
                <p className={styles.upsellSubscriptionTermText}>{selectedPlanDescription}</p>
                <div className={styles.pricesContainer}>
                  <p className={styles.upsellSubscriptionPriceText}>
                    {'$'}
                    {Math.ceil(centsToDollarsNumber(chargedTodayCents))}
                  </p>
                  {hasDiscount && (
                    <p className={styles.currentSubscriptionPriceText}>
                      {'$'}
                      {centsToDollarsNumber(currentMTMPriceCents)}
                    </p>
                  )}
                </div>
                {hasDiscount && (
                  <p className={styles.savingsText}>
                    <Trans
                      i18nKey={
                        creditAppliedCents > 0
                          ? 'youll_save_amount_over_the_next_months_months_with_rollover_credit'
                          : 'youll_save_amount_over_the_next_months_months'
                      }
                      values={{
                        amount: Math.floor(centsToDollarsNumber(currentMTMPriceCents - chargedTodayCents)),
                        months: selectedSubscriptionOption?.renewalMonths,
                        creditAmount: creditAppliedCents > 0 ? Math.round(centsToDollarsNumber(creditAppliedCents)) : 0,
                      }}
                      components={{
                        credit: <span />,
                      }}
                    />
                  </p>
                )}
                <Image className={styles.checkmark} image={{ type: 'image', url: Check }} />
              </div>
            </div>
            <div className={styles.collarContainer}>
              <Image className={styles.collarImage} image={{ type: 'image', url: CollarHero }} />
            </div>
            <MonthlyUpgradeConfirmationModal
              onContinue={() => upgradeMembership()}
              subscriptionOption={selectedSubscriptionOption}
              previewRenewalDate={previewRenewalDate}
              open={upgradeClicked && !error}
              upgradeNow={couponCode ? true : false}
              trigger={
                <Button
                  className={styles.simpleButton}
                  disabled={checkoutDisabled}
                  onClick={() => {
                    events.upgradePlan({ newSku: selectedSubscriptionSku });
                    setUpgradeClicked(true);
                  }}
                >
                  {t('claim_offer')}
                </Button>
              }
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <div className={styles.container}>
          <h1 className={styles.mainHeader}>{t('upgrade_your_membership')}</h1>
          <div className={styles.sectionHeaderContainer}>
            <h2 className={styles.sectionHeader}>{t('current_plan')}</h2>
          </div>
          <div className={styles.currentPlanContainer}>
            <div>
              <div className={styles.currentPlanUnit}>{t('month_to_month')}</div>
              <div className={styles.currentPlanRate}>{t('rate_per_week', { rate: displayRate })}</div>
            </div>
          </div>
          <div className={styles.sectionHeaderContainer}>
            <h2 className={styles.sectionHeader}>{t('select_a_new_plan')}</h2>
          </div>
          {applicableSubscriptionProducts.length === 0 ? (
            <p>{t('no_plans_available_please_contact_support_if_you_think_this_is_an_error')}</p>
          ) : (
            <>
              {' '}
              <Chooser
                onSelect={(selectedSku) => {
                  if (!selectedSku) {
                    return;
                  }

                  dispatch(
                    cartActions.setCartItem({
                      moduleId: device.moduleId,
                      sku: selectedSku,
                      currency,
                    }),
                  );
                }}
                selectedOption={selectedSubscriptionSku}
                groups={chooserGroups}
              />
              <div className={classNames(styles.stickyCTA, styles.productAction)}>
                {selectedPlanDescription && (
                  <div className={styles.selectedPlanDescription}>{selectedPlanDescription}</div>
                )}
                <MonthlyUpgradeConfirmationModal
                  onContinue={() => upgradeMembership()}
                  subscriptionOption={selectedSubscriptionOption}
                  previewRenewalDate={previewRenewalDate}
                  open={upgradeClicked && !error} // Close popup if there's an error when user tries to upgrade
                  trigger={
                    <Button
                      className={styles.button}
                      disabled={checkoutDisabled}
                      onClick={() => {
                        events.upgradePlan({ newSku: selectedSubscriptionSku });
                        setUpgradeClicked(true);
                      }}
                    >
                      {selectedSubscriptionOption ? t('upgrade_plan') : t('pick_your_membership')}
                    </Button>
                  }
                />
                {error && <ErrorMessage errors={[error]} errorID={errorID} />}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
